import React, { useReducer, useEffect, useCallback } from 'react';
import L from 'leaflet';
import { Map, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  TemperatureDisplay, 
  HumidityDisplay, 
  MoistureDisplay,
  PressureDisplay, 
  VoltageDisplay,
  ConductivityDisplay,
  RelayDisplay,
  DistanceDisplay,
  HeightDisplay,
  GenericGaugeDisplay
} from './SensorVisualisations';
import StorageTankDisplay from './StorageTankDisplay';
import { getSensorTypeLabel } from './doorSensorUtils';
import logo from '../../../assets/images/logo.png';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom TV Icon Component
const TvIcon = ({ style = {} }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    style={{ width: '1em', height: '1em', ...style }}
  >
    <path d="M21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h5v2h8v-2h5c1.1 0 1.99-.9 1.99-2L23 5c0-1.1-.9-2-2-2zm0 14H3V5h18v12z"/>
  </svg>
);

// Create custom asset tracking icon
const createAssetIcon = (speed = 0) => {
  const speedText = Math.round(speed).toString();
  const svgContent = `
    <svg width="75" height="87.5" viewBox="0 0 300 350" xmlns="http://www.w3.org/2000/svg">
      <path d="M225 137.5 C225 89.2 185.8 50 137.5 50 S50 89.2 50 137.5 c0 48.3 87.5 192.5 87.5 192.5 s87.5-144.2 87.5-192.5 z" fill="#0d3825"/>
      <circle cx="137.5" cy="137.5" r="58.3" fill="white" stroke="white" stroke-width="14"/>
      <circle cx="137.5" cy="137.5" r="42" fill="white"/>
      <circle cx="220" cy="80" r="30" fill="#0d3825"/>
      <text x="220" y="88" text-anchor="middle" font-family="Arial, sans-serif" font-size="24" fill="white" font-weight="bold">${speedText}</text>
    </svg>
  `;
  
  return new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(svgContent),
    iconSize: [60, 70],
    iconAnchor: [37.5, 87.5],
    popupAnchor: [0, -87.5]
  });
};

// Resolution configurations
const RESOLUTIONS = {
  '1920x1080': { width: 1920, height: 1080, cols: 4, sensorsPerPage: 16 },
  '2560x1440': { width: 2560, height: 1440, cols: 6, sensorsPerPage: 24 },
  '3840x2160': { width: 3840, height: 2160, cols: 8, sensorsPerPage: 32 }
};

// Action types
const ACTION_TYPES = {
  SET_RESOLUTION: 'SET_RESOLUTION',
  SET_FULLSCREEN: 'SET_FULLSCREEN',
  SET_DEVICES: 'SET_DEVICES',
  ADD_DEVICES: 'ADD_DEVICES',
  SET_LOADING: 'SET_LOADING',
  SET_REFRESH_COUNTDOWN: 'SET_REFRESH_COUNTDOWN',
  SET_PAGE_CHANGE_COUNTDOWN: 'SET_PAGE_CHANGE_COUNTDOWN',
  SET_TOTAL_LOADED: 'SET_TOTAL_LOADED',
  SET_TOTAL_DEVICES: 'SET_TOTAL_DEVICES',
  SET_CURRENT_PAGE: 'SET_CURRENT_PAGE',
  NEXT_PAGE: 'NEXT_PAGE',
  RESET_PAGE: 'RESET_PAGE',
  DECREMENT_REFRESH: 'DECREMENT_REFRESH',
  DECREMENT_PAGE_CHANGE: 'DECREMENT_PAGE_CHANGE',
  RESET_REFRESH: 'RESET_REFRESH',
  RESET_PAGE_CHANGE: 'RESET_PAGE_CHANGE'
};

// Initial state
const initialState = {
  resolution: '3840x2160',
  isFullscreen: false,
  allDevices: [],
  loading: false,
  refreshCountdown: 300, // 5 minutes
  pageChangeCountdown: 20, // 20 seconds per page
  totalLoaded: 0,
  totalDevices: 0,
  currentPage: 0
};

// Reducer function
function tvDisplayReducer(state, action) {
  switch (action.type) {
    case ACTION_TYPES.SET_RESOLUTION:
      return { ...state, resolution: action.payload };
      
    case ACTION_TYPES.SET_FULLSCREEN:
      return { ...state, isFullscreen: action.payload };
      
    case ACTION_TYPES.SET_DEVICES:
      return { 
        ...state, 
        allDevices: action.payload,
        currentPage: 0,
        pageChangeCountdown: 20
      };
      
    case ACTION_TYPES.ADD_DEVICES:
      return { 
        ...state, 
        allDevices: [...state.allDevices, ...action.payload]
      };
      
    case ACTION_TYPES.SET_LOADING:
      return { ...state, loading: action.payload };
      
    case ACTION_TYPES.SET_TOTAL_LOADED:
      return { ...state, totalLoaded: action.payload };
      
    case ACTION_TYPES.SET_TOTAL_DEVICES:
      return { ...state, totalDevices: action.payload };
      
    case ACTION_TYPES.SET_CURRENT_PAGE:
      return { 
        ...state, 
        currentPage: action.payload,
        pageChangeCountdown: 20
      };
      
    case ACTION_TYPES.NEXT_PAGE:
      return { 
        ...state, 
        currentPage: (state.currentPage + 1) % action.payload.totalPages,
        pageChangeCountdown: 20
      };
      
    case ACTION_TYPES.RESET_PAGE:
      return { 
        ...state, 
        currentPage: 0,
        pageChangeCountdown: 20
      };
      
    case ACTION_TYPES.DECREMENT_REFRESH:
      return { 
        ...state, 
        refreshCountdown: state.refreshCountdown > 0 ? state.refreshCountdown - 1 : 0
      };
      
    case ACTION_TYPES.DECREMENT_PAGE_CHANGE:
      return { 
        ...state, 
        pageChangeCountdown: state.pageChangeCountdown > 0 ? state.pageChangeCountdown - 1 : 0
      };
      
    case ACTION_TYPES.RESET_REFRESH:
      return { ...state, refreshCountdown: 300 };
      
    case ACTION_TYPES.RESET_PAGE_CHANGE:
      return { ...state, pageChangeCountdown: 20 };
      
    case ACTION_TYPES.SET_REFRESH_COUNTDOWN:
      return { ...state, refreshCountdown: action.payload };
      
    case ACTION_TYPES.SET_PAGE_CHANGE_COUNTDOWN:
      return { ...state, pageChangeCountdown: action.payload };
      
    default:
      return state;
  }
}

const TVDisplayMode = ({ 
  onExit, 
  devices: initialDevices,
  fetchDevices,
  fetchViewData,
  timezone,
  startDate,
  endDate,
  selectedViewId,
  currentViewName
}) => {
  const [state, dispatch] = useReducer(tvDisplayReducer, {
    ...initialState,
    allDevices: initialDevices || []
  });

  const currentRes = RESOLUTIONS[state.resolution];
  const SENSORS_PER_PAGE = currentRes.sensorsPerPage;

  // Helper functions
  const getLastKnownValue = (sensor, sensorType) => {
    if (!sensor.timelines || !sensor.timelines.length) return null;
    for (let i = sensor.timelines.length - 1; i >= 0; i--) {
      const timeline = sensor.timelines[i];
      if (timeline[sensorType]) {
        return {
          value: timeline[sensorType].value,
          unit: timeline[sensorType].unit || '',
          time: timeline[sensorType].time
        };
      }
    }
    return null;
  };

  const getLastKnownLocation = (sensor) => {
    if (!sensor.timelines || !sensor.timelines.length) return null;
    let latestLocation = null;
    let latestTimestamp = 0;
    sensor.timelines.forEach(timeline => {
      const lat = timeline.latitude?.value;
      const lng = timeline.longitude?.value;
      const speed = timeline.speed?.value || 0;
      const time = timeline.latitude?.time || timeline.longitude?.time;
      if (lat && lng && lat !== 0 && lng !== 0 && time) {
        const timestamp = new Date(time).getTime();
        if (timestamp > latestTimestamp) {
          latestTimestamp = timestamp;
          latestLocation = { lat, lng, speed, time };
        }
      }
    });
    return latestLocation;
  };

  const getSensorTypes = (sensor) => {
    if (!sensor.timelines || !sensor.timelines.length) return [];
    const types = new Set();
    sensor.timelines.forEach(timeline => {
      Object.keys(timeline).forEach(key => {
        if (key !== 'time' && key !== 'latitude' && key !== 'longitude' && key !== 'speed') {
          types.add(key);
        }
      });
    });
    return Array.from(types);
  };

  const isConfiguredStorageTank = (sensor) => {
    if (!sensor.asset || sensor.asset.category !== 'storage') return false;
    const asset = sensor.asset;
    const hasValidConfig = 
      asset.height_at_100 != null &&
      asset.litres_at_100 != null &&
      asset.height_at_0 != null &&
      asset.litres_at_0 != null &&
      !isNaN(parseFloat(asset.height_at_100)) &&
      !isNaN(parseFloat(asset.litres_at_100)) &&
      !isNaN(parseFloat(asset.height_at_0)) &&
      !isNaN(parseFloat(asset.litres_at_0));
    if (!hasValidConfig) return false;
    const hasDistanceData = sensor.timelines?.some(timeline => 
      timeline.distance && timeline.distance.value != null
    );
    return hasDistanceData;
  };

  // Flatten devices into individual sensor type cards
  const allSensorCards = state.allDevices.flatMap(device => 
    (device.sensors || []).flatMap(sensor => {
      const sensorTypes = getSensorTypes(sensor);
      const isAssetTracking = sensor.part_number === 'ASSET_TRACKING';
      const isStorageTank = isConfiguredStorageTank(sensor);
      
      if (isAssetTracking || isStorageTank) {
        return [{ device, sensor, sensorType: isAssetTracking ? 'asset_tracking' : 'storage_tank', isSpecial: true }];
      }
      
      if (sensorTypes.length === 0) {
        return [{ device, sensor, sensorType: null, isSpecial: false }];
      }
      
      return sensorTypes.map(sensorType => ({
        device,
        sensor,
        sensorType,
        isSpecial: false
      }));
    })
  );

  const totalPages = Math.ceil(allSensorCards.length / SENSORS_PER_PAGE);
  const currentPageSensors = allSensorCards.slice(
    state.currentPage * SENSORS_PER_PAGE, 
    (state.currentPage + 1) * SENSORS_PER_PAGE
  );

  // Load all devices
  const loadAllDevices = useCallback(async () => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
    dispatch({ type: ACTION_TYPES.SET_DEVICES, payload: [] });
    
    if (selectedViewId && fetchViewData) {
      try {
        const result = await fetchViewData(selectedViewId);
        if (result && result.devices) {
          dispatch({ type: ACTION_TYPES.SET_DEVICES, payload: result.devices });
          dispatch({ type: ACTION_TYPES.SET_TOTAL_LOADED, payload: result.devices.length });
          dispatch({ type: ACTION_TYPES.SET_TOTAL_DEVICES, payload: result.total_item || result.devices.length });
        }
      } catch (error) {
        console.error('Error loading view data:', error);
      }
      dispatch({ type: ACTION_TYPES.SET_LOADING, payload: false });
      return;
    }

    let allLoadedDevices = [];
    let page = 1;
    let hasMore = true;
    const pageSize = 50;

    while (hasMore) {
      try {
        const result = await fetchDevices({
          page,
          pageSize,
          startDate,
          endDate,
          timezone
        });

        if (result && result.devices && result.devices.length > 0) {
          allLoadedDevices = [...allLoadedDevices, ...result.devices];
          dispatch({ type: ACTION_TYPES.SET_DEVICES, payload: allLoadedDevices });
          dispatch({ type: ACTION_TYPES.SET_TOTAL_LOADED, payload: allLoadedDevices.length });
          dispatch({ type: ACTION_TYPES.SET_TOTAL_DEVICES, payload: result.total_item || allLoadedDevices.length });
          
          hasMore = page < (result.total_page || 1);
          page++;
          
          if (hasMore) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        } else {
          hasMore = false;
        }
      } catch (error) {
        console.error('Error loading devices:', error);
        hasMore = false;
      }
    }

    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: false });
  }, [fetchDevices, fetchViewData, selectedViewId, startDate, endDate, timezone]);

  // Initial load
  useEffect(() => {
    loadAllDevices();
  }, [loadAllDevices]);

  // Refresh countdown and auto-reload (5 minutes)
  useEffect(() => {
    const timer = setInterval(() => {
      dispatch({ type: ACTION_TYPES.DECREMENT_REFRESH });
      
      // Check if countdown reached 0
      if (state.refreshCountdown <= 1) {
        loadAllDevices();
        dispatch({ type: ACTION_TYPES.RESET_REFRESH });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [state.refreshCountdown, loadAllDevices]);

  // Page change countdown
  useEffect(() => {
    if (totalPages <= 1) return;

    const timer = setInterval(() => {
      dispatch({ type: ACTION_TYPES.DECREMENT_PAGE_CHANGE });
      
      // Check if countdown reached 0
      if (state.pageChangeCountdown <= 1) {
        dispatch({ type: ACTION_TYPES.NEXT_PAGE, payload: { totalPages } });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [state.pageChangeCountdown, totalPages]);

  // Fullscreen handling
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      dispatch({ type: ACTION_TYPES.SET_FULLSCREEN, payload: true });
    } else {
      document.exitFullscreen();
      dispatch({ type: ACTION_TYPES.SET_FULLSCREEN, payload: false });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      dispatch({ type: ACTION_TYPES.SET_FULLSCREEN, payload: !!document.fullscreenElement });
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const renderAssetTrackingMap = (sensor) => {
    const location = getLastKnownLocation(sensor);
    if (!location) {
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '6px', 
          marginTop: '8px' 
        }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>📍</div>
          <div style={{ fontSize: '12px', color: '#999' }}>No location data</div>
        </div>
      );
    }

    return (
      <div style={{ marginTop: '8px', height: '200px', borderRadius: '6px', overflow: 'hidden' }}>
        <Map 
          center={[location.lat, location.lng]} 
          zoom={15} 
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
          attributionControl={false}
        >
          <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
          <Marker position={[location.lat, location.lng]} icon={createAssetIcon(location.speed)}>
            <Popup>
              <div>
                <strong>{sensor.asset?.title || sensor.name || 'Asset'}</strong><br />
                <strong>Last Seen:</strong> {location.time}<br />
                <strong>Speed:</strong> {Math.round(location.speed)} km/h<br />
                <strong>Coordinates:</strong> {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
              </div>
            </Popup>
          </Marker>
        </Map>
      </div>
    );
  };

  const renderSensorCard = ({ device, sensor, sensorType, isSpecial }, index) => {
    const isStorageTank = sensorType === 'storage_tank';
    const isAssetTracking = sensorType === 'asset_tracking';
    const hasData = sensorType !== null;

    return (
      <div
        key={`${device.id}-${sensor.id}-${sensorType}-${index}`}
        style={{
          background: 'white',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          border: '1px solid rgba(255,255,255,0.9)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          height: '100%'
        }}
      >
        {/* Header - compact */}
        <div style={{ marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
          {device.device_name && (
            <div style={{ fontSize: '9px', color: '#999', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {device.device_name}
            </div>
          )}
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#0B2A1B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {sensor.name || 'Sensor'}
          </div>
          {sensor.asset && (
            <div style={{ fontSize: '9px', color: '#666', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Asset: {sensor.asset.title}
            </div>
          )}
          {sensorType && !isSpecial && (
            <div style={{ 
              fontSize: '10px', 
              color: '#0B2A1B', 
              marginTop: '4px', 
              fontWeight: '600',
              textTransform: 'capitalize',
              whiteSpace: 'nowrap', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis'
            }}>
              {getSensorTypeLabel(sensorType).toLowerCase()}
            </div>
          )}
          {isStorageTank && (
            <div style={{ 
              fontSize: '10px', 
              color: '#0B2A1B', 
              marginTop: '4px', 
              fontWeight: '600'
            }}>
              Storage Tank
            </div>
          )}
          {isAssetTracking && (
            <div style={{ 
              fontSize: '10px', 
              color: '#0B2A1B', 
              marginTop: '4px', 
              fontWeight: '600'
            }}>
              Asset Tracking
            </div>
          )}
        </div>

        {/* Content - single sensor type display */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {hasData ? (
            isStorageTank ? (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <StorageTankDisplay sensor={sensor} asset={sensor.asset} compact={true} />
              </div>
            ) : isAssetTracking ? (
              <div style={{ width: '100%', height: '100%' }}>
                {renderAssetTrackingMap(sensor)}
              </div>
            ) : sensorType === 'temperature' ? (
              <TemperatureDisplay sensor={sensor} compact={true} />
            ) : sensorType === 'humidity' ? (
              <HumidityDisplay sensor={sensor} compact={true} />
            ) : sensorType === 'moisture' ? (
              <MoistureDisplay sensor={sensor} compact={true} />
            ) : sensorType === 'pressure' ? (
              <PressureDisplay sensor={sensor} compact={true} />
            ) : sensorType === 'voltage' ? (
              <VoltageDisplay sensor={sensor} compact={true} />
            ) : sensorType === 'conductivity' ? (
              <ConductivityDisplay sensor={sensor} compact={true} />
            ) : sensorType === 'relay' ? (
              <RelayDisplay sensor={sensor} compact={true} />
            ) : sensorType === 'distance' ? (
              <DistanceDisplay sensor={sensor} compact={true} />
            ) : sensorType === 'height' ? (
              <HeightDisplay sensor={sensor} compact={true} />
            ) : (
              (() => {
                const lastValue = getLastKnownValue(sensor, sensorType);
                if (!lastValue) return null;
                
                let minValue = 0;
                let maxValue = 100;
                let label = getSensorTypeLabel(sensorType);
                
                switch(sensorType.toLowerCase()) {
                  case 'air_quality':
                  case 'co2':
                    maxValue = 5000;
                    break;
                  case 'light':
                  case 'lux':
                    maxValue = 10000;
                    break;
                  case 'uv':
                    maxValue = 15;
                    break;
                  case 'pm2.5':
                  case 'pm10':
                    maxValue = 500;
                    break;
                  case 'vibration':
                    maxValue = 10;
                    break;
                  case 'noise':
                  case 'sound':
                    maxValue = 120;
                    break;
                  default:
                    const value = parseFloat(lastValue.value);
                    if (value > 100) {
                      maxValue = Math.ceil(value * 1.5);
                    }
                }
                
                return (
                  <GenericGaugeDisplay 
                    sensor={sensor}
                    sensorType={sensorType}
                    label={label}
                    minValue={minValue}
                    maxValue={maxValue}
                    compact={true}
                  />
                );
              })()
            )
          ) : (
            <div style={{ 
              color: '#999',
              fontSize: '12px',
              fontStyle: 'italic'
            }}>
              No data available
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: '#0B2A1B',
      zIndex: 9999,
      overflow: 'hidden'
    }}>
      {/* Control Bar */}
      <div style={{
        position: 'sticky',
        top: 0,
        background: 'rgba(11, 42, 27, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        zIndex: 10,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
      }}>
        <img src={logo} alt="Logo" style={{ 
          height: '32px', 
          width: 'auto',
          filter: 'brightness(0) invert(1)',
          opacity: 0.95
        }} />
        <div style={{ flex: 1 }}>
          {selectedViewId && currentViewName && (
            <div style={{ fontSize: '14px', color: 'white', fontWeight: '500' }}>
              View: {currentViewName}
            </div>
          )}
        </div>

        <select
          value={state.resolution}
          onChange={(e) => dispatch({ type: ACTION_TYPES.SET_RESOLUTION, payload: e.target.value })}
          style={{
            width: '160px',
            height: '36px',
            borderRadius: '6px',
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.1)',
            color: 'white',
            fontSize: '13px',
            padding: '0 12px',
            cursor: 'pointer'
          }}
        >
          <option value="1920x1080">1920×1080 (4×4)</option>
          <option value="2560x1440">2560×1440 (6×4)</option>
          <option value="3840x2160">3840×2160 (8×4)</option>
        </select>

        {totalPages > 1 && (
          <div style={{ 
            background: 'rgba(255,255,255,0.15)', 
            padding: '8px 16px', 
            borderRadius: '6px',
            color: 'white',
            fontSize: '13px',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            Page {state.currentPage + 1}/{totalPages} · Next in {state.pageChangeCountdown}s
          </div>
        )}

        <div style={{ 
          background: 'rgba(255,255,255,0.15)', 
          padding: '8px 16px', 
          borderRadius: '6px',
          color: 'white',
          fontSize: '13px',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          Refresh in {state.refreshCountdown}s
        </div>

        {state.loading && (
          <div style={{ color: 'white', fontSize: '13px' }}>
            Loading {state.totalLoaded}/{state.totalDevices}...
          </div>
        )}

        <button
          onClick={toggleFullscreen}
          style={{
            height: '36px',
            padding: '0 16px',
            borderRadius: '6px',
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.1)',
            color: 'white',
            fontSize: '13px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          {state.isFullscreen ? '⛶ Exit' : '⛶ Full'}
        </button>

        <button
          onClick={onExit}
          style={{
            height: '36px',
            padding: '0 16px',
            borderRadius: '6px',
            border: '1px solid #ff4d4f',
            background: '#ff4d4f',
            color: 'white',
            fontSize: '13px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Exit
        </button>
      </div>

      {/* Sensor Grid */}
      <div style={{ 
        padding: '16px',
        height: 'calc(100vh - 64px)',
        overflow: 'hidden'
      }}>
        {state.loading && state.allDevices.length === 0 ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            height: '100%',
            flexDirection: 'column',
            gap: '20px'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid rgba(255, 255, 255, 0.3)',
              borderTop: '4px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
              {selectedViewId ? 'Loading view sensors...' : 'Loading sensors...'}
            </span>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${currentRes.cols}, 1fr)`,
            gridTemplateRows: 'repeat(4, 1fr)',
            gap: '12px',
            width: '100%',
            height: '100%'
          }}>
            {currentPageSensors.map((sensorCard, idx) => 
              renderSensorCard(sensorCard, idx)
            )}
          </div>
        )}

        {!state.loading && allSensorCards.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px',
            color: 'rgba(255,255,255,0.7)',
            fontSize: '18px',
            fontStyle: 'italic'
          }}>
            {selectedViewId ? 'No sensors in this view' : 'No sensors available'}
          </div>
        )}
      </div>
    </div>
  );
};

export default TVDisplayMode;